import React, { useCallback, useContext, useState } from 'react';
import clsx from 'clsx';
import {
	useNotification,
	useRequestDocumentModals,
	useSendDocumentModals,
	useSendFollowUp,
	useSendMessageToAllModals,
} from '@/hooks';
import { useBulkRemoveFromCase } from './useBulkRemoveFromCase';
import {
	PhaseName,
	RequestDocumentData,
	RequestDocumentToClientsPayload,
	RequestDocumentItemMap,
	RejectClient,
} from '@/interfaces';
import { CaseContext } from '@/context/CaseContext';
import { caseDocumentsService } from '@/api/caseDocuments';
import useModal from '@/hooks/useModal';
import { FieldValues } from 'react-hook-form';
import { Button, Icon } from '@/components/common';
import Icons from '@/const/icons';
import { ModalHeader } from '@/components/modals/ModalHeader';
import { FlagClientsBulkModal, RemoveFlagBulkModal } from '@/components/modals';
import { useQueryClient } from '@tanstack/react-query';
import {
	actionRequiredService,
	lawyerDirectoryService,
	GET_CLIENT_PROFILE_ACTIONS_REQUIRED,
	GET_CLIENT_PROFILE_FILES,
	GET_FOLLOW_UP,
	GET_CLIENT_DIRECTORY,
} from '@/api';
import { SendMessageToAllPayload } from '@/interfaces/inbox';
import { inboxService } from '@/api/inbox';
import { useCaseClientFilters } from './useCaseClientFilters';

export const useCaseClientActions = (
	initialUserIds: (string | null)[] = []
) => {
	const { parsedFilters } = useCaseClientFilters();
	const { caseDetails, isCaseReadOnly } = useContext(CaseContext);
	const { toastError, toastSuccess } = useNotification();

	const [selectedUserIds, setSelectedUserIds] =
		useState<(string | null)[]>(initialUserIds);
	const [applicationIdByUserId, setApplicationIdByUserId] = useState<
		Record<string, number>
	>({});
	const queryClient = useQueryClient();

	const selectedCountForRemove = selectedUserIds.includes(null)
		? // Select All targets all filter matches; toast/title stay plural unless
		  // only one loaded id exists and selection is a single explicit user.
		  Math.max(Object.keys(applicationIdByUserId).length, 2)
		: selectedUserIds.filter((id): id is string => !!id).length;

	const onBulkRemoveSuccessRef = React.useRef<(() => void) | undefined>();
	const handleBulkRemoveSuccess = useCallback(() => {
		onBulkRemoveSuccessRef.current?.();
	}, []);

	const {
		ModalBulkRemove,
		showBulkRemoveModal,
		hideBulkRemoveModal,
		onConfirmBulkRemove,
		isRemoving: isRemovingFromCase,
	} = useBulkRemoveFromCase({
		selectedUserIds,
		setSelectedUserIds,
		caseId: caseDetails?.id || '',
		parsedFilters,
		applicationIdByUserId,
		selectedCount: selectedCountForRemove,
		onSuccess: handleBulkRemoveSuccess,
	});
	const {
		Modal: ModalDocumentSent,
		show: showModalDocumentSent,
		hide: hideModalDocumentSent,
	} = useModal();

	const {
		onOpenRequestDocuments,
		renderRequestDocumentModals,
		setIsOpenRequestDocuments,
	} = useRequestDocumentModals({
		caseName: caseDetails?.title || '',
		onSubmit: (data) => onSubmitRequestDocument(data),
		selectedUserIds,
		setSelectedUserIds,
		phaseName: (caseDetails?.phase_name as PhaseName) || '',
		isCaseReadOnly,
	});

	const {
		onOpenRequestDocuments: onOpenRequestDocumentsAll,
		renderRequestDocumentModals: renderRequestDocumentModalsAll,
		setIsOpenRequestDocuments: setIsOpenRequestDocumentsAll,
	} = useRequestDocumentModals({
		caseName: caseDetails?.title || '',
		isToAllUsers: true,
		onSubmit: (data) => onSubmitRequestDocument(data),
		selectedUserIds,
		setSelectedUserIds,
		phaseName: (caseDetails?.phase_name as PhaseName) || '',
		isCaseReadOnly,
	});

	const { onOpenSendDocument, renderSendDocumentModals } =
		useSendDocumentModals({
			caseId: caseDetails?.id || '',
			caseName: caseDetails?.title || '',
			phaseName: (caseDetails?.phase_name as PhaseName) || '',
			selectedUserIds,
			setSelectedUserIds,
		});

	const {
		onOpenSendDocument: onOpenSendDocumentAll,
		renderSendDocumentModals: renderSendDocumentModalsAll,
		setIsOpenSendDocument: setIsOpenSendDocumentAll,
	} = useSendDocumentModals({
		caseId: caseDetails?.id || '',
		caseName: caseDetails?.title || '',
		isToAllUsers: true,
		phaseName: (caseDetails?.phase_name as PhaseName) || '',
		selectedUserIds,
		setSelectedUserIds,
	});

	const {
		onOpenSendMessageToAll,
		renderSendMessageToAllModals,
		hideModalSendMessageToAll,
	} = useSendMessageToAllModals({
		onSubmit: (data) => onSubmitSendMessageToAll(data),
		selectedUserIds,
		setSelectedUserIds,
		phaseName: (caseDetails?.phase_name as PhaseName) || '',
	});

	const { onOpenSendFollowUp, renderSendFollowUpModals } = useSendFollowUp({
		onSubmit: (data) => onSubmitSendFollowUp(data),
	});

	const {
		Modal: ModalFlagClients,
		show: onOpenFlagClients,
		hide: hideModalFlagClients,
	} = useModal();

	const {
		Modal: ModalRemoveFlag,
		show: onRemoveFlagClients,
		hide: hideModalRemoveFlag,
	} = useModal();

	const [isDeleteFlagModalOpen, setIsDeleteFlagModalOpen] = useState(false);

	const onSubmitRequestDocument = useCallback(
		async (data: RequestDocumentData) => {
			try {
				const criteria = data.request_question.map((item) => {
					return {
						...item,
						criteria_type: RequestDocumentItemMap[item.criteria_type],
					};
				});

				const newData: RequestDocumentToClientsPayload = {
					document_name: data.document_name || '',
					skip_document: data.skip_document,
					users_ids: selectedUserIds.includes(null)
						? []
						: (selectedUserIds as string[]) || [],
					case_id: caseDetails?.id || '',
					criteria,
					email_text: data.message?.replaceAll('\n', '<br/>') || undefined,
					...parsedFilters,
				};
				await caseDocumentsService.postRequestDocument(newData);
				queryClient.invalidateQueries({
					queryKey: [GET_CLIENT_PROFILE_FILES],
				});
				queryClient.invalidateQueries({
					queryKey: [GET_CLIENT_PROFILE_ACTIONS_REQUIRED],
				});
				showModalDocumentSent();
			} catch (error) {
				toastError('The document could not be requested. Please try again.');
			} finally {
				setIsOpenRequestDocuments(false);
				setIsOpenRequestDocumentsAll(false);
			}
		},
		[
			selectedUserIds,
			caseDetails?.id,
			parsedFilters,
			showModalDocumentSent,
			toastError,
			setIsOpenRequestDocuments,
			setIsOpenRequestDocumentsAll,
		]
	);

	const onSubmitSendMessageToAll = useCallback(
		async (data: FieldValues) => {
			try {
				const newData: SendMessageToAllPayload = {
					case_id: caseDetails?.id || '',
					content: data.message || '',
					users_ids: selectedUserIds.includes(null)
						? []
						: (selectedUserIds as string[]) || [],
					...parsedFilters,
				};
				await inboxService.postSendMessageToAll(newData);
				toastSuccess('The message has been sent successfully.');
			} catch (error) {
				toastError('The message could not be sent. Please try again.');
			} finally {
				hideModalSendMessageToAll();
			}
		},
		[toastSuccess, toastError, hideModalSendMessageToAll]
	);

	const onSubmitSendFollowUp = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		async (data: any) => {
			try {
				const finalData = {
					...data,
					case_id: caseDetails?.id || '',
					users_ids: selectedUserIds.includes(null)
						? []
						: (selectedUserIds as string[]) || [],
					...parsedFilters,
				};
				await actionRequiredService.postSendFollowUp(finalData);
				toastSuccess('The follow-up has been sent successfully.');
				queryClient.invalidateQueries({
					queryKey: [GET_FOLLOW_UP, caseDetails?.id],
				});
			} catch (error) {
				toastError('An error occurred. Please try again.');
			}
		},
		[caseDetails?.id, selectedUserIds, parsedFilters, toastSuccess, toastError]
	);

	const onSubmitRejectClient = useCallback(
		async (isRejected: boolean, callback?: () => void) => {
			try {
				const newData: RejectClient = {
					caseId: caseDetails?.id || '',
					users_ids: selectedUserIds.includes(null)
						? []
						: (selectedUserIds as string[]) || [],
					...parsedFilters,
					rejected: isRejected,
				};
				await lawyerDirectoryService.postRejectClient(newData);
				toastSuccess(
					isRejected
						? 'Clients have been marked as rejected.'
						: 'Clients have been reinstated successfully.'
				);
				queryClient.invalidateQueries({
					queryKey: [GET_CLIENT_DIRECTORY],
				});
				setSelectedUserIds([]);
				if (callback) callback();
			} catch (error) {
				toastError('An error occurred. Please try again.');
			}
		},
		[
			caseDetails?.id,
			selectedUserIds,
			parsedFilters,
			toastSuccess,
			toastError,
			queryClient,
			setSelectedUserIds,
		]
	);

	const handleOpenRequestDocuments = useCallback(() => {
		if (initialUserIds.length > 0) {
			setSelectedUserIds(initialUserIds);
		}
		onOpenRequestDocuments();
	}, [initialUserIds, onOpenRequestDocuments]);

	const handleOpenSendDocument = useCallback(() => {
		if (initialUserIds.length > 0) {
			setSelectedUserIds(initialUserIds);
		}
		onOpenSendDocument();
	}, [initialUserIds, onOpenSendDocument]);

	const handleOpenSendMessageToAll = useCallback(() => {
		if (initialUserIds.length > 0) {
			setSelectedUserIds(initialUserIds);
		}
		onOpenSendMessageToAll();
	}, [initialUserIds, onOpenSendMessageToAll]);

	const onOpenSendDocumentAllSkipUsers = useCallback(() => {
		setIsOpenSendDocumentAll(true);
	}, [setIsOpenSendDocumentAll]);

	const onOpenRequestDocumentsAllSkipUsers = useCallback(() => {
		setIsOpenRequestDocumentsAll(true);
	}, [setIsOpenRequestDocumentsAll]);

	const setOnBulkRemoveSuccess = useCallback((callback?: () => void) => {
		onBulkRemoveSuccessRef.current = callback;
	}, []);

	return {
		selectedUserIds,
		setSelectedUserIds,
		setApplicationIdByUserId,
		setOnBulkRemoveSuccess,
		onSubmitRejectClient,
		onOpenRequestDocuments: handleOpenRequestDocuments,
		onOpenRequestDocumentsAll,
		onOpenRequestDocumentsAllSkipUsers,
		onOpenSendDocument: handleOpenSendDocument,
		onOpenSendDocumentAll,
		onOpenSendDocumentAllSkipUsers,
		onOpenSendMessageToAll: handleOpenSendMessageToAll,
		onOpenSendFollowUp,
		onOpenFlagClients,
		onRemoveFlagClients,
		ModalBulkRemove,
		showBulkRemoveModal,
		hideBulkRemoveModal,
		onConfirmBulkRemove,
		isRemovingFromCase,
		renderModals: (
			<>
				{renderRequestDocumentModals}
				{renderRequestDocumentModalsAll}
				{renderSendDocumentModals}
				{renderSendDocumentModalsAll}
				{renderSendMessageToAllModals}
				{renderSendFollowUpModals}
				<ModalFlagClients
					title={isDeleteFlagModalOpen ? '' : 'Flag for review'}
					classNameTitle="text-left"
					classNameCardContainer={clsx(
						'w-full md:!w-[632px] md:!max-w-[632px] md:min-h-[616px] md:flex md:flex-col',
						{
							'!p-0 !shadow-none !bg-transparent': isDeleteFlagModalOpen,
						}
					)}
					classNameContainerChildren="!overflow-visible"
					backgroundColor="white"
					withXButton={!isDeleteFlagModalOpen}
				>
					<FlagClientsBulkModal
						caseId={caseDetails?.id || ''}
						userIds={selectedUserIds}
						parsedFilters={parsedFilters}
						hide={hideModalFlagClients}
						onSuccess={() => setSelectedUserIds([])}
						onDeleteModalOpenChange={setIsDeleteFlagModalOpen}
					/>
				</ModalFlagClients>
				<ModalRemoveFlag
					title="Remove Flag"
					classNameTitle="text-left"
					classNameCardContainer="w-full md:!w-[632px] md:!max-w-[632px] md:min-h-[616px] md:flex md:flex-col"
					classNameContainerChildren="!overflow-visible"
					backgroundColor="white"
					withXButton
				>
					<RemoveFlagBulkModal
						caseId={caseDetails?.id || ''}
						userIds={selectedUserIds}
						parsedFilters={parsedFilters}
						hide={hideModalRemoveFlag}
						onSuccess={() => setSelectedUserIds([])}
					/>
				</ModalRemoveFlag>
				<ModalDocumentSent
					withXButton
					title={<ModalHeader title="Document sent" />}
					classNameContainerChildren="w-full md:w-[532px]"
					backgroundColor="white"
				>
					<div className="flex flex-col items-center justify-center">
						<Icon src={Icons.checkCircleEmptyState} width={150} height={150} />
						<Button
							label="Close"
							size="fit"
							decoration="secondary"
							onClick={hideModalDocumentSent}
						/>
					</div>
				</ModalDocumentSent>
			</>
		),
	};
};
